/**
 * ProfileSetupPage.js
 * Onboarding profile setup — shown after plan selection. A single screen:
 * your sport (one per profile) + timezone, then straight into the app.
 * The old second step (skill level, goals, playing style, development
 * focus) was removed 2026-10-01 — lighter onboarding converts better, and
 * none of it was load-bearing: skill/style are better read from actual
 * match data than a one-time self-report, and development focus is now
 * what Season milestones are for. See
 * docs_zenanalytics/13-PROFILE-SIMPLIFICATION-2026-10-01.md.
 */

import React, { useState } from 'react';
import axios from 'axios';
import supabase from '../supabaseClient';
import { ZenLogo } from '../landing/LandingPage';
import { RACKET_SPORTS, browserTimezone, listTimezones, browserLocale, listLocales, localeLabel } from '../utils/profileOptions';
import { COUNTRIES, countryDefaults } from '../utils/countryData';

const API  = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const bg     = '#060d1a';
const bg1    = '#0d1829';
const border = '#1a2d47';
const text   = '#ffffff';
const muted  = '#94a3b8';
const dim    = '#64748b';
const accent = '#0ea5e9';
const font   = "'Inter', system-ui, -apple-system, sans-serif";


function ContinueBtn({ label = 'Continue', onClick, disabled, loading }) {
  return (
    <div style={{
      position: 'sticky', bottom: 0,
      background: `linear-gradient(to top, ${bg} 70%, transparent)`,
      padding: '24px 24px 32px',
    }}>
      <button
        onClick={onClick}
        disabled={disabled || loading}
        style={{
          width: '100%', padding: '18px',
          background: disabled ? border : accent,
          color: disabled ? dim : '#fff',
          border: 'none', borderRadius: '50px',
          fontSize: '17px', fontWeight: '700',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontFamily: font, transition: 'background 0.15s, transform 0.1s',
          letterSpacing: '-0.2px',
        }}
        onMouseEnter={e => { if (!disabled) e.currentTarget.style.transform = 'translateY(-1px)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
      >
        {loading ? 'Saving…' : label}
      </button>
    </div>
  );
}

function TileList({ items, value, onChange, twoLine }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {items.map(item => {
        const active = value === item.value;
        return (
          <button key={item.value} type="button" onClick={() => onChange(item.value)} style={{
            display: 'flex', alignItems: twoLine ? 'flex-start' : 'center',
            gap: '16px', padding: twoLine ? '18px 20px' : '20px 20px',
            background: active ? `${accent}18` : bg1,
            border: `2px solid ${active ? accent : border}`,
            borderRadius: '16px', color: active ? text : muted,
            fontSize: '17px', fontWeight: active ? '700' : '500',
            fontFamily: font, cursor: 'pointer', textAlign: 'left',
            transition: 'all 0.15s', width: '100%',
          }}>
            <span style={{ fontSize: '24px', width: '28px', textAlign: 'center', flexShrink: 0, marginTop: twoLine ? '1px' : 0 }}>
              {item.icon}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ lineHeight: 1.3 }}>{item.label}</div>
              {twoLine && item.sub && (
                <div style={{ fontSize: '13px', color: active ? muted : dim, fontWeight: '400', marginTop: '3px', lineHeight: 1.4 }}>
                  {item.sub}
                </div>
              )}
            </div>
            {active && <span style={{ marginLeft: 'auto', color: accent, fontSize: '20px', flexShrink: 0 }}>✓</span>}
          </button>
        );
      })}
    </div>
  );
}

// ── Sport + country + timezone + locale — the whole of onboarding now ──────────
function StepEssentials({
  sport, setSport, country, onCountryChange, timezone, setTimezone, locale, setLocale,
  onFinish, saving,
}) {
  const selectStyle = {
    width: '100%', boxSizing: 'border-box', background: bg1, border: `1px solid ${border}`,
    borderRadius: '16px', color: text, padding: '16px 18px', fontSize: '16px',
    fontFamily: font, outline: 'none', marginBottom: '8px',
  };
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, overflowY: 'auto', padding: '32px 24px 0' }}>
        <h1 style={{ color: text, fontSize: '30px', fontWeight: '800', margin: '0 0 12px', lineHeight: 1.15, letterSpacing: '-0.5px' }}>
          Which sport do you play?
        </h1>
        <p style={{ color: muted, fontSize: '15px', lineHeight: 1.6, margin: '0 0 24px' }}>
          One sport per profile keeps your matches, stats and coaching focused.
        </p>
        <TileList items={RACKET_SPORTS} value={sport} onChange={setSport} />

        <p style={{ color: muted, fontSize: '13px', fontWeight: '600', margin: '28px 0 8px' }}>Your country</p>
        <select value={country} onChange={e => onCountryChange(e.target.value)} style={selectStyle}>
          <option value="">Select country...</option>
          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <p style={{ color: dim, fontSize: '12px', margin: '0 0 20px', lineHeight: 1.5 }}>
          Sets sensible defaults for your timezone and date format below — both stay yours to change.
        </p>

        <p style={{ color: muted, fontSize: '13px', fontWeight: '600', margin: '20px 0 8px' }}>Your timezone</p>
        <select value={timezone} onChange={e => setTimezone(e.target.value)} style={selectStyle}>
          {listTimezones().map(z => <option key={z} value={z}>{z}</option>)}
        </select>
        <p style={{ color: dim, fontSize: '12px', margin: '0 0 20px', lineHeight: 1.5 }}>
          It decides what "today" means for your matches and coach. Change it if you're
          travelling or based somewhere other than your country above.
        </p>

        <p style={{ color: muted, fontSize: '13px', fontWeight: '600', margin: '20px 0 8px' }}>Date format</p>
        <select value={locale} onChange={e => setLocale(e.target.value)} style={selectStyle}>
          {!listLocales().includes(locale) && <option value={locale}>{localeLabel(locale)}</option>}
          {listLocales().map(l => <option key={l} value={l}>{localeLabel(l)}</option>)}
        </select>
        <p style={{ color: dim, fontSize: '12px', margin: '0 0 24px', lineHeight: 1.5 }}>
          How dates and numbers are written across the app. The app itself is always in English.
        </p>
      </div>
      <ContinueBtn label="Finish →" onClick={onFinish} disabled={!sport || !timezone} loading={saving} />
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function ProfileSetupPage({ user, onContinue }) {
  const meta       = user?.user_metadata || {};
  const googleName = meta.full_name || meta.name || '';
  const nameParts  = googleName.trim().split(' ');
  const firstName  = meta.given_name || nameParts[0] || '';
  const lastName   = nameParts.slice(1).join(' ') || '';

  const [sport,        setSport]        = useState('');
  const [country,      setCountry]      = useState('');
  const [timezone,     setTimezone]     = useState(browserTimezone());
  const [locale,       setLocale]       = useState(browserLocale());
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState(null);

  // Country sets sensible defaults for timezone + locale, but both stay
  // separately editable right below it — plenty of people live in one
  // country and are temporarily on a different timezone.
  const handleCountryChange = (value) => {
    setCountry(value);
    const defaults = countryDefaults(value);
    if (defaults) {
      setTimezone(defaults.timezone);
      setLocale(defaults.locale);
    }
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const authHeaders = { headers: { Authorization: `Bearer ${session?.access_token}` } };

      const fullName = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
      if (fullName || country) {
        const profileUpdate = {};
        if (fullName) profileUpdate.name = fullName;
        if (country) profileUpdate.country = country;
        await axios.patch(`${API}/profiles/${user.id}`, profileUpdate, authHeaders).catch(() =>
          axios.post(`${API}/profiles`, { user_id: user.id, ...profileUpdate }, authHeaders)
        );
      }

      const zcPayload = { user_id: user.id, primary_sport: sport, game_plan_sports: sport, timezone, locale };
      if (fullName) zcPayload.name = fullName;
      await axios.post(`${API}/profiles/player-profile`, zcPayload, authHeaders);

      onContinue();
    } catch (err) {
      console.error('Profile save failed:', err);
      setError('Could not save your profile. Please try again.');
      setSaving(false);
    }
  };


  const BG_PROFILE = 'https://images.unsplash.com/photo-1528543606781-2f6e6857f318?auto=format&fit=crop&w=1920&q=80';

  return (
    <div style={{ minHeight: '100vh', fontFamily: font, display: 'flex', flexDirection: 'column', width: '100%', position: 'relative' }}>
      <div style={{
        position: 'fixed', inset: 0,
        backgroundImage: `linear-gradient(180deg, rgba(6,13,26,0.50) 0%, rgba(6,13,26,0.45) 50%, rgba(6,13,26,0.55) 100%), url(${BG_PROFILE})`,
        backgroundSize: 'cover', backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat', filter: 'saturate(1.3) brightness(1.1)', zIndex: 0,
      }} />

      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10,
        background: 'rgba(6,13,26,0.85)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${border}`, height: '64px',
        display: 'flex', alignItems: 'center', padding: '0 24px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <ZenLogo size={28} />
          <div>
            <span style={{ color: text, fontWeight: '800', fontSize: '16px', letterSpacing: '-0.4px', display: 'block', lineHeight: 1.1 }}>ZenAnalytics</span>
            <span style={{ color: dim, fontSize: '10px', letterSpacing: '0.05em', lineHeight: 1 }}>Train on Insight</span>
          </div>
        </div>
      </nav>

      <div style={{
        maxWidth: '480px', margin: '0 auto', width: '100%', flex: 1,
        display: 'flex', flexDirection: 'column',
        position: 'relative', zIndex: 2,
        paddingTop: '64px',
        background: 'rgba(6,13,26,0.75)',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
      }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <StepEssentials
            sport={sport} setSport={setSport}
            country={country} onCountryChange={handleCountryChange}
            timezone={timezone} setTimezone={setTimezone}
            locale={locale} setLocale={setLocale}
            onFinish={handleSave} saving={saving}
          />
        </div>

        {error && (
          <p style={{ color: '#f87171', fontSize: '13px', textAlign: 'center', padding: '0 24px 16px', margin: 0 }}>{error}</p>
        )}
      </div>
    </div>
  );
}
